import {
  AppUser,
  checkClaimsWeb,
  EventrDate,
  getV2ValueWP,
  ICostingList,
  IEventrActivity,
  IEventrFlight,
  IEventrTransport,
  INextComponent,
  IOptionsWithTypes,
  isDateInPast,
  isIEventrFlight,
  isUserPartOfUsersWithTags,
  ITodoList,
  ITodoTask,
  IValueData,
  parseTodoLabel,
  SectionId,
  spError,
  spErrorTags,
  spLog,
  structuredCloneL,
  TItineraryEntry,
  tryParseJSON,
  TZoneTypes,
  useItinInfo,
  V2,
} from "@eventr/common";
import { parseISO } from "date-fns";
import isPast from "date-fns/isPast";
import i18n from "i18n/i18next";
import { cloneDeep } from "lodash";
import { createSelector } from "reselect";
import { currentTodoGroup, todoStatusForCurrentGroup } from "v2/utils/activities";
import { StateCreator } from "zustand";
import { shallow } from "zustand/shallow";
import { DataAPI } from "../../data/Data";
import { isFavourited } from "../../feat/favourites/favourite-state";
import { useGuestStore } from "../../feat/guest/guest.store";
import { isIosNativeNavigation } from "../../feat/nativeNavigation/platform";
import {
  filterOutPendingTag,
  getDelayPromise,
  getTimezoneOfItinerary,
  isIEventrDatePast,
  updateSection,
} from "../../utils";
import { DraftItem, mergeDraftItems } from "../../utils/merge-draft-items";
import { TodoRange } from "../../v2/componentCards/todoCard";
import { ScreenType } from "../../v2/renderers/rendererLocator";
import { generateUpNextList, sortByDate } from "../../v2/upNextRenderer";
import { categoriesActivities, getDataArray, splitImportedData, splitTodosData } from "../../v2/v2Utils";
import { BoundStore, bs, useBoundStore } from "../bound.store";
import { IItinerary } from "../interfaces";
import { flushStoredWrites } from "../indexdbStorage";
import { useLanguageContext } from "../languageState/languageContext";
import { defaultAddSnack } from "../overlayState/overlayContext";
import { useOverlayStore } from "../overlayState/overlay.store";
import { EItineraryLoadState } from "../pageState/pageState.slice";
import { useStorageStore } from "../storage/storage.store";
import { es, useEphemeralStore } from "../useEphemeralStore";
import { useUserPrefsStore } from "../userPrefsState/userPrefs.store";
import { CurrentItinSlice, defaultCurrentItinSlice, SectionUpdateResult } from "./currentItin.interfaces";
import { getItineraryMapByID, replaceRenderOptions } from "./itineraryMap.slice";
import { endItineraryCreation } from "./new-itinerary-flow";
import { coverImageUrl } from "feat/media/cover-image";

const EDIT_VALUES_FRESH_MS = 30_000;
let editValuesHolders = 0;
let editValuesFetched: { key: string; at: number } | undefined;
let editValuesInFlight: { key: string; request: Promise<void> } | undefined;

/** Whether the cached copy of an itinerary matches the version its map entry names. */
const isUpToDate = (cached: IItinerary | undefined, mapEntry: NonNullable<ReturnType<typeof getItineraryMapByID>>) =>
  cached !== undefined && cached.version.toString() === mapEntry.itinerary_version.toString();

const editValuesKey = () => `${bs().selectedOrg?.name}:${bs().selectedItinID}`;

/** Marks editValues as needed until the returned release runs, fetching them if stale. */
export const holdEditValues = () => {
  editValuesHolders++;
  void bs().ensureEditValues();
  return () => {
    editValuesHolders--;
  };
};

export const createCurrentItinSlice: StateCreator<BoundStore, [["zustand/devtools", never]], [], CurrentItinSlice> = (
  set,
  get,
) => ({
  ...defaultCurrentItinSlice,
  setCurrentItin: async (id: number) => {
    const { selectedItinID } = get();
    // Selecting an itinerary ends any create flow, whichever way the form was left.
    if (id !== -1) endItineraryCreation();
    // Keying by itinerary is what
    // makes a cross-itinerary read inexpressible; this clear is a separate, deliberate
    // choice about how long unsaved work lives. Leaving an itinerary discards it rather
    // than holding it for a return that may never come.
    if (selectedItinID !== id) {
      set(
        { draftSectionItems: {}, draftSectionItemStagedAt: {}, expenseFormDrafts: {} },
        false,
        "setCurrentItin-clearDrafts",
      );
    }
    useEphemeralStore.setState({
      selectedLngLatLabelledItineraryComponents: null,
      selectedLngLatLabelledTeamMember: null,
      generalInfoIsSelected: false,
      mapDrawerIsPresented: false,
      // A newly picked itinerary's map opens on the whole of it, whichever mode the recentre
      // control was left in for the last one.
      ...(selectedItinID !== id ? { mapFocusMode: "itinerary" as const } : {}),
    });

    await get().loadItinerary(id);
  },
  prefetchItinerary: (id: number) => {
    const mapEntry = getItineraryMapByID(id);
    if (!mapEntry || isUpToDate(get().itineraryData[id], mapEntry)) return;
    void bs().pullEventData(id);
  },
  refreshCurrentItinerary: async () => {
    await get().loadItinerary(get().selectedItinID);
  },
  loadItinerary: async (id: number) => {
    try {
      es().setItineraryLoadState(EItineraryLoadState.Loading);

      // selectedItinCode.value = getSelectedItinCode()
      const mapEntry = getItineraryMapByID(id);
      if (!mapEntry) {
        if (id === -1) {
          set({ selectedItinerary: undefined, selectedItinID: id }, false, "loadItinerary");
        } else {
          spError("Selected itinerary not found in map");
        }
        es().setItineraryLoadState(EItineraryLoadState.Loaded);
        return;
      }

      const localItineraryData = get().itineraryData[id];

      // The choice is committed now, not when the fetch below returns. On a fresh login
      // nothing is cached, so every swipe waits on the network, and until selectedItinID
      // moves every other tab's carousel still points at the old itinerary — changing tab
      // mid-fetch showed it in the header. Whatever local copy exists stands in meanwhile;
      // with none, the itineraryData subscription fills selectedItinerary in when the
      // fetch lands.
      set(
        {
          selectedItinerary: localItineraryData ? replaceRenderOptions(localItineraryData) : undefined,
          selectedItinID: id,
        },
        false,
        "loadItinerary-select",
      );

      if (isUpToDate(localItineraryData, mapEntry)) {
        es().setItineraryLoadState(EItineraryLoadState.Loaded);
        return;
      }

      spLog(
        localItineraryData ? "Need to update local version" : "Selected itinerary not found in local store, fetch it",
        id,
      );
      const eventData = await bs().pullEventData(id);

      // Swiping on before the fetch returns starts another load. That one owns the
      // selection and the load state now; writing this result would drag the selection
      // back to an itinerary the user has already left.
      if (get().selectedItinID !== id) return;

      if (eventData) {
        set({ selectedItinerary: replaceRenderOptions(eventData) }, false, "loadItinerary-fetched");
        es().setItineraryLoadState(EItineraryLoadState.Loaded);
      } else {
        es().setItineraryLoadState(EItineraryLoadState.Error);
      }
    } catch (error) {
      spErrorTags(error, ["DataProvider"]);
      es().setItineraryLoadState(EItineraryLoadState.Error);
    }
  },
  setPast: (past) => {
    set({ past }, false, "setPast");
  },
  getEventInfoV2: () => {
    if (get().selectedItinerary) {
      return get().selectedItinerary?.v2?.find((d) => {
        return d.sectionName === "event_information";
      });
    }
    return undefined;
  },
  getCurrentEventImageUrl: () => {
    return coverImageUrl(get().getEventInfoV2()?.data, "medium");
  },
  filterEntitiesUserSpecific: (screenType: ScreenType, data: V2, userList?: string, viewedUserId?: number | null) => {
    const { isUserPartOfSelection, getUserFromID } = get();
    const visibilityPath = (data.rendererOptions?.settings as any)?.visibilityPath;

    const arrayData = getDataArray(data);

    const filteredData: (TItineraryEntry | undefined)[] = [];
    // this is a hacky way to beable to use 2 lists of users, maybe rework to be x lists
    let userListPath = [];
    let userListPath2: string[] = [];
    if (userList?.includes("&123&")) {
      const parts = userList.split("&123&");
      userListPath = parts[0]!.split(".");
      userListPath2 = parts[1]!.split(".");
    } else {
      userListPath = userList?.split(".") || [];
    }
    //const userListPath = userList?.split('.')
    if (screenType === "personal" && userList) {
      for (let i = 0; i < arrayData.length; i++) {
        const itineraryEntry = arrayData[i];
        let ignore = false;
        if (visibilityPath) {
          if (getV2ValueWP(visibilityPath, itineraryEntry) === "Public") {
            ignore = true;
          } else {
            ignore = false;
          }
        }

        if (
          ignore ||
          isUserPartOfSelection(getAllUsersCl(userListPath ?? [], itineraryEntry)) ||
          isUserPartOfSelection(getAllUsersCl(userListPath2 ?? [], itineraryEntry))
        ) {
          filteredData.push(itineraryEntry);
        }
      }
    } else if (screenType === "teamMember" && userList) {
      for (let i = 0; i < arrayData.length; i++) {
        const e = arrayData[i];
        let ignore = false;
        if (visibilityPath) {
          if (getV2ValueWP(visibilityPath, e) === "Public") {
            ignore = true;
          } else {
            ignore = false;
          }
        }
        if (viewedUserId != null) {
          const user = getUserFromID(viewedUserId);
          const users = getAllUsersCl(userListPath ?? [], e);
          if (
            ignore ||
            (user && isUserPartOfUsersWithTags(user, users)) ||
            isUserPartOfSelection(getAllUsersCl(userListPath2 ?? [], e))
          ) {
            filteredData.push(e);
          }
        }
      }
    } else {
      try {
        filteredData.push(...(arrayData || []));
      } catch (error) {
        spErrorTags(error, ["DataProvider"], arrayData);
      }
    }
    return filteredData;
  },
  getSectionV2FromSectionId: (sectionId: number) => {
    if (get().selectedItinerary) {
      return get().selectedItinerary?.v2?.find((d) => {
        return d.sectionID === sectionId;
      });
    }
    return undefined;
  },
  updateItinerarySection: async (sectionId: number, newSectionData: TItineraryEntry): Promise<SectionUpdateResult> => {
    const { selectedItinerary, forceItineraryMapUpdate, selectedItinID } = get();
    // The overlay store's `loading` drives a full-screen Backdrop mounted once per pushed native view.
    // Writing it fires a synchronous, unbatched re-render across every mounted view's
    // Backdrop at once, which can collide with an in-flight native view transition and
    // corrupt the shared fiber tree app-wide. Skip it under native nav; callers show
    // their own local saving state instead.
    if (!isIosNativeNavigation()) useOverlayStore.getState().setLoading(true);
    try {
      if (!newSectionData || !selectedItinID) {
        defaultAddSnack("Bad data or no selected itinerary.", "error");
        if (!isIosNativeNavigation()) useOverlayStore.getState().setLoading(false);
        return "error";
      }
      if (sectionId === SectionId.FLIGHT && isIEventrFlight(newSectionData)) {
        //Flight section
        const flight = cloneDeep(newSectionData);
        const originalCheckIn = flight.check_in;
        const x = new EventrDate(flight.departure.dep_time);
        x.subtract({ hours: 2 });
        const computedCheckIn = x.dateTimeString;
        if (originalCheckIn !== computedCheckIn) {
          newSectionData = {
            ...newSectionData,
            check_in: computedCheckIn,
          };
        }
      }
      if (sectionId !== SectionId.GENERAL) {
        const resp = await DataAPI.updateSection(selectedItinID, sectionId.toString(), {
          comparison: "_id",
          [sectionId.toString()]: { data: [newSectionData] },
        });

        if (resp?.status === "error") {
          if (resp.message?.includes("private data")) {
            return "private_data_error";
          }
          return "error";
        }

        const newSection = updateSection(selectedItinerary ?? ({} as IItinerary), newSectionData, sectionId);
        set({ selectedItinerary: newSection }, false, "updateItinerarySection");
      } else {
        await DataAPI.createUpdateGeneralInfo({
          itinerary_id: selectedItinID,
          section_id: SectionId.GENERAL,
          value: newSectionData,
        });
      }
      await get().pullEditValues();
      await forceItineraryMapUpdate();

      // The GENERAL branch above writes through to the server without updating
      // selectedItinerary. Ordered after forceItineraryMapUpdate so the version check sees
      // the new version.
      if (sectionId === SectionId.GENERAL) {
        await get().refreshCurrentItinerary();
      }
    } catch (error) {
      spErrorTags(error, ["DataProvider"]);
      return "error";
    } finally {
      if (!isIosNativeNavigation()) useOverlayStore.getState().setLoading(false);
    }
    return "success";
  },
  deleteItineraryItem: async (sectionId: number, itemId: string) => {
    const { forceItineraryMapUpdate, selectedItinID } = get();
    if (sectionId && selectedItinID) {
      await DataAPI.deleteBooking(itemId, sectionId.toString(), selectedItinID);
      await get().pullEditValues();
      await forceItineraryMapUpdate();
      return true;
    } else {
      defaultAddSnack("Bad data or no selected itinerary.", "error");
      return false;
    }
  },
  getV2Value: (valueLocator: string, data: any) => {
    const { getCurrentUser } = get();
    if (!valueLocator) return "";
    return getV2ValueWP(valueLocator, data, getCurrentUser());
  },
  getSectionV2FromSectionIdFromSelectedItinerary: (sectionId: number) => {
    const { selectedItinerary } = get();
    if (selectedItinerary && selectedItinerary.v2) {
      const tmpV2 = selectedItinerary.v2.find((v2Data) => {
        return v2Data.sectionID === sectionId;
      });
      if (tmpV2) {
        return tmpV2;
      } else {
        return undefined;
      }
    } else {
      return undefined;
    }
  },
  getApiUpdates: async () => {
    const flightSection = get().selectedItinerary?.v2?.find((d) => d.sectionID === SectionId.FLIGHT);
    const flights = flightSection?.data?.flights ?? [];

    const flightsList = flights.map((f: IEventrFlight) => {
      return {
        flight_id: f.flight_id,
        flight_version: f.flight_version,
        _id: f._id,
      };
    });
    // need to store in a variable so that during rappid switching it does not get lost
    const currentItin = get().selectedItinID;

    const updates = await DataAPI.getAPIUpdates(flightsList, currentItin);

    if ((updates.data?.original?.length ?? 0) > 0) {
      await updates.data?.original?.forEach(async (update) => {
        const selectedOrgName = get().selectedOrg?.name;
        if (selectedOrgName) {
          await DataAPI.guest.duplicateItinerary(selectedOrgName, {
            code: update.codeInfo.code,
            itin_to_copy_to: currentItin,
          });
        }
      });
      await bs().forceItineraryMapUpdate();
    }
    set({ apiUpdates: updates.data ?? {} }, false, "getApiUpdates");
  },
  removePublicItinerary: async (itinCode: string) => {
    const { selectedItinID, forceItineraryMapUpdate } = get();
    useOverlayStore.getState().setLoading(true);
    try {
      await DataAPI.itineraries.removePublicItin(selectedItinID, itinCode);
      await forceItineraryMapUpdate();
    } catch (error) {
      spErrorTags(error, ["DataProvider"]);
    } finally {
      useOverlayStore.getState().setLoading(false);
    }
  },
  updateTodo: async (
    task: ITodoTask,
    groupId: string,
    itineraryItemId: string,
    //  singleSelect?: boolean
  ) => {
    const taskId = task._id as string;
    const status = !task.completed;
    const fillerId = task.fillerId;
    let groupCosts: ICostingList = {};
    if (task.costs && task.costs.length > 0) {
      const updatedCosts = task.costs.map((cost) => {
        const costWithRef = { ...cost, entity_id: groupId, item_ref: `task-${itineraryItemId}-${groupId}-${taskId}` };
        //Depending on the state of the todo, we either add it with a cost or delete it by marking it with deleted: true, the backend will handle the rest
        if (status) {
          return costWithRef;
        } else {
          return { ...costWithRef, deleted: true };
        }
      });
      groupCosts = {
        [groupId as string]: {
          costs: updatedCosts,
        },
      };
    }

    try {
      const { selectedItinID } = get();
      const stringStatus = status ? "completed" : "incomplete";
      const delayPromise = getDelayPromise(1000);
      const [resp] = await Promise.all([
        await DataAPI.todo.upsertTodo({
          filler: { status: stringStatus, group_id: groupId },
          object_id: taskId,
          filler_id: fillerId,
          itinerary_id: selectedItinID,
          costs: groupCosts,
          // singleSelect: singleSelect,
        }),
        delayPromise,
      ]);
      if (resp.status === "success") {
        set(
          {
            apiUpdates: {
              ...get().apiUpdates,
              todos: {
                ...(get().apiUpdates?.todos ?? {}),
                [taskId]: {
                  id: resp.data.id,
                  value: {
                    status: stringStatus,
                    group_id: groupId,
                    user_id: resp.data.created_by,
                  },
                },
              },
            },
          },
          false,
          "updateTodo",
        );
        if (task.costs && task.costs.length > 0 && resp.data?.cost?.success === false) {
          defaultAddSnack(i18n.t("cost.sections.8.todoList-snackbar-fail"), "warning");
          spErrorTags("Error adding todo cost:", ["TODO"], resp.data.costing);
        }
      } else {
        defaultAddSnack(i18n.t("sections.8.todoList.snackbar-fail"), "error");
        spErrorTags("Error adding todo cost:", ["TODO"], resp.data);
      }
    } catch (error) {
      spErrorTags("Error updating todo:", ["TODO"], error);
    }
  },
  pullEditValues: () => {
    const { selectedItinID } = get();
    const key = editValuesKey();
    const request: Promise<void> = (async () => {
      try {
        // Nothing is selected, so the previous itinerary's values have to go: selectEditItem
        // falls back to them whenever an item has no draft.
        if (selectedItinID === -1) {
          set({ editValues: undefined, editValuesUnavailable: true }, false, "pullEditValuesNoItinerary");
          return;
        }
        if (!checkClaimsWeb([`/${selectedItinID}`], ["editItinerary"])) {
          set({ editValues: undefined, editValuesUnavailable: true }, false, "pullEditValuesNotAllowed");
          return;
        }
        // A retry after a failure shows as loading again rather than as the failure.
        if (get().editValuesUnavailable) set({ editValuesUnavailable: false }, false, "pullEditValuesRetry");
        const resp = await DataAPI.editValues.getAll(selectedItinID);
        // The user moved to another itinerary or organisation while this was in flight.
        if (editValuesKey() !== key) return;
        if (resp.status === "success") {
          const data = (resp.data ?? {}) as Partial<IValueData>;

          const normalised: IValueData = {
            [SectionId.GENERAL]: data[SectionId.GENERAL] ?? {},
            [SectionId.FLIGHT]: data[SectionId.FLIGHT],
            [SectionId.HOTEL]: data[SectionId.HOTEL],
            [SectionId.HIRE_CAR]: data[SectionId.HIRE_CAR],
            [SectionId.TRANSPORT]: {
              ...data[SectionId.TRANSPORT],
              transport: data[SectionId.TRANSPORT]?.transport ?? [],
            },
            [SectionId.ACTIVITY]: {
              ...data[SectionId.ACTIVITY],
              activities: data[SectionId.ACTIVITY]?.activities ?? [],
            },
          };

          set({ editValues: normalised, editValuesUnavailable: false }, false, "pullEditValues");
          editValuesFetched = { key, at: Date.now() };
        } else {
          set({ editValuesUnavailable: true }, false, "pullEditValuesFailed");
        }
      } catch (error) {
        spErrorTags(error, ["DataProvider"]);
        if (editValuesKey() !== key) return;
        set({ editValues: undefined, editValuesUnavailable: true }, false, "pullEditValuesError");
      }
    })().finally(() => {
      if (editValuesInFlight?.request === request) editValuesInFlight = undefined;
    });
    editValuesInFlight = { key, request };
    return request;
  },
  ensureEditValues: () => {
    const key = editValuesKey();
    if (editValuesInFlight?.key === key) return editValuesInFlight.request;
    if (editValuesFetched?.key === key && Date.now() - editValuesFetched.at < EDIT_VALUES_FRESH_MS) {
      return Promise.resolve();
    }
    return get().pullEditValues();
  },
  upsertElement: async (sectionID: number, element: any) => {
    const { selectedItinID, forceItineraryMapUpdate } = get();
    const resp = await DataAPI.editValues.upsertElement(selectedItinID, sectionID, element);
    if (resp.status !== "success") {
      spError("Error upserting element");
      return resp;
    } else {
      await get().pullEditValues();
      await forceItineraryMapUpdate();
    }
    return resp;
  },
  addDraftSectionItem: (sectionId: SectionId, item: DraftItem) => {
    const { draftSectionItems, draftSectionItemStagedAt, selectedItinID } = get();
    const itinDrafts = draftSectionItems[selectedItinID] ?? {};
    const stagedAtByItin = draftSectionItemStagedAt[selectedItinID] ?? {};
    const stagedAtBySection = stagedAtByItin[sectionId] ?? {};
    const existing = itinDrafts[sectionId] ?? [];
    const index = existing.findIndex((draft) => draft._id === item._id);
    // No serialised-equality guard here: every draft write originates from a user event,
    // not an effect, so there is no re-entrant write to protect against. A guard that
    // compares JSON.stringify output treats a field cleared to undefined the same as a
    // field that was never set, since serialising drops undefined keys - that silently
    // dropped the write that was supposed to persist the clear.
    const updated = index === -1 ? [...existing, item] : existing.map((draft, i) => (i === index ? item : draft));
    set(
      {
        draftSectionItems: {
          ...draftSectionItems,
          [selectedItinID]: { ...itinDrafts, [sectionId]: updated },
        },
        draftSectionItemStagedAt: {
          ...draftSectionItemStagedAt,
          [selectedItinID]: {
            ...stagedAtByItin,
            [sectionId]: { ...stagedAtBySection, [item._id]: Date.now() },
          },
        },
      },
      false,
      "addDraftSectionItem",
    );
    return flushStoredWrites();
  },
  removeDraftSectionItem: (sectionId: SectionId, itemId: string) => {
    const { draftSectionItems, draftSectionItemStagedAt, selectedItinID } = get();
    const itinDrafts = draftSectionItems[selectedItinID];
    const existing = itinDrafts?.[sectionId];
    if (!existing?.length) {
      return Promise.resolve();
    }
    const remaining = existing.filter((draft) => draft._id !== itemId);
    if (remaining.length === existing.length) {
      return Promise.resolve();
    }
    const stagedAtByItin = draftSectionItemStagedAt[selectedItinID] ?? {};
    const stagedAtBySection = { ...(stagedAtByItin[sectionId] ?? {}) };
    delete stagedAtBySection[itemId];
    set(
      {
        draftSectionItems: {
          ...draftSectionItems,
          [selectedItinID]: { ...itinDrafts, [sectionId]: remaining },
        },
        draftSectionItemStagedAt: {
          ...draftSectionItemStagedAt,
          [selectedItinID]: { ...stagedAtByItin, [sectionId]: stagedAtBySection },
        },
      },
      false,
      "removeDraftSectionItem",
    );
    return flushStoredWrites();
  },
  removeAllDraftSectionItems: (sectionId: SectionId) => {
    const { draftSectionItems, draftSectionItemStagedAt, selectedItinID } = get();
    const itinDrafts = draftSectionItems[selectedItinID];
    if (!itinDrafts?.[sectionId]?.length) {
      return Promise.resolve();
    }
    const stagedAtByItin = draftSectionItemStagedAt[selectedItinID] ?? {};
    set(
      {
        draftSectionItems: {
          ...draftSectionItems,
          [selectedItinID]: { ...itinDrafts, [sectionId]: [] },
        },
        draftSectionItemStagedAt: {
          ...draftSectionItemStagedAt,
          [selectedItinID]: { ...stagedAtByItin, [sectionId]: {} },
        },
      },
      false,
      "removeAllDraftSectionItems",
    );
    return flushStoredWrites();
  },
  deleteElement: async (sectionID: number, itemId?: string) => {
    const { selectedItinID, forceItineraryMapUpdate } = get();
    const resp = await DataAPI.editValues.deleteElement(selectedItinID, sectionID, itemId);
    if (resp.status !== "success") {
      spError("Error deleting element");
      return resp;
    } else {
      await get().pullEditValues();
      await forceItineraryMapUpdate();
    }
    return resp;
  },
  setLastUsedDateTime: (dateTime: string) => {
    set({ lastUsedDateTime: dateTime }, false, "setLastUsedDateTime");
  },
});

// MARK: Subscriptions

let subscriptionsRegistered = false;

// Registration waits for rehydration to finish, so the first `fireImmediately` run reads the
// persisted selectedItinID rather than the -1 default that pullRocketChatChannels ignores.
function registerCurrentItinSubscriptions() {
  if (subscriptionsRegistered) return;
  subscriptionsRegistered = true;

  useBoundStore.subscribe(
    (state) => ({
      itin: state.selectedItinerary,
      refreshKey: state.refreshKey,
    }),
    ({ itin, refreshKey }) => {
      spLog("selectedItin subscription called", refreshKey);

      let eventEnded = false;
      if (itin && itin.v2?.[0]?.data.end_date) {
        eventEnded = isDateInPast(itin.v2[0].data.end_date);
      }
      useBoundStore.setState({ eventEnded: eventEnded });
      // set itin timezone
      if (itin) {
        useItinInfo.getState().setTimezone(getTimezoneOfItinerary(itin));
      }
      const user = bs().wpUser;
      if (itin) {
        generateItineraryComponents(itin, useUserPrefsStore.getState().showDestTime, user);
      }
    },
    { fireImmediately: true, equalityFn: shallow },
  );
  let previousItinID: number | undefined;
  useBoundStore.subscribe(
    (state) => ({
      itinID: state.selectedItinID,
      refreshKey: state.refreshKey,
    }),
    ({ itinID, refreshKey }) => {
      spLog("selectedItinID subscription called", itinID, refreshKey);
      // editValues belong to the selected itinerary or are unset.
      if (previousItinID !== undefined && previousItinID !== itinID) {
        editValuesFetched = undefined;
        useBoundStore.setState({ editValues: undefined, editValuesUnavailable: false });
      }
      previousItinID = itinID;
      if (editValuesHolders > 0) void bs().ensureEditValues();
      bs().getApiUpdates();
      bs().pullRocketChatChannels();
    },
    { fireImmediately: true, equalityFn: shallow },
  );
  useBoundStore.subscribe(
    (state: BoundStore) => state.itineraryComponents,
    (state) => {
      spLog("itineraryComponents subscription called ");
      setSelectedItinEmpty(state);
    },
    { fireImmediately: true, equalityFn: shallow },
  );
}

if (useStorageStore.getState().hasHydrated === "finished") {
  registerCurrentItinSubscriptions();
} else {
  const unsubscribeFromHydration = useStorageStore.subscribe((state) => {
    if (state.hasHydrated !== "finished") return;
    unsubscribeFromHydration();
    registerCurrentItinSubscriptions();
  });
}

// MARK: Selectors

export const selectPendingTodoCount = createSelector(
  (state: BoundStore) => state.apiUpdates?.todos,
  (state: BoundStore, todoList: ITodoList) => todoList,
  (todoStatus, todoList: IEventrActivity) => {
    if (todoStatus && todoList) {
      const pendingTodos = (todoList?.tasks ?? []).filter((todo) => {
        if (!todo._id) return false;
        // Exclude | label-only items (no checkbox)
        if (parseTodoLabel(todo.name).hideCheckbox) return false;

        const currentGroup = currentTodoGroup(todoList?.attendees_list ?? [], bs().wpUser?.id);
        const todoStatusForGroup = todoStatusForCurrentGroup(todoStatus, currentGroup);
        return (
          todoStatusForGroup?.[todo._id] === undefined || todoStatusForGroup?.[todo._id]?.value?.status === "incomplete"
        );
      });
      return pendingTodos.length;
    }
    return 0;
  },
);

export const selectAllEditTodos = createSelector(
  (state: BoundStore) => state.editValues?.[SectionId.ACTIVITY],
  (state: BoundStore) => state.draftSectionItems[state.selectedItinID]?.[SectionId.ACTIVITY],
  (activities, drafts) => {
    const merged = mergeDraftItems(activities, "activities", drafts);
    const mergedActivities = merged?.activities as IEventrActivity[] | undefined;
    return mergedActivities?.filter((activity) => activity.type === "todo") ?? [];
  },
);
export const selectAllEditGeneralActivities = createSelector(
  (state: BoundStore) => state.editValues?.[SectionId.ACTIVITY],
  (state: BoundStore) => state.draftSectionItems[state.selectedItinID]?.[SectionId.ACTIVITY],
  (activities, drafts) => {
    const merged = mergeDraftItems(activities, "activities", drafts);
    const mergedActivities = merged?.activities as IEventrActivity[] | undefined;
    return mergedActivities?.filter((activity) => activity.type !== "todo" && activity._og_code === undefined) ?? [];
  },
);
export const selectAllEditTransport = createSelector(
  (state: BoundStore) => state.editValues?.[SectionId.TRANSPORT],
  (state: BoundStore) => state.draftSectionItems[state.selectedItinID]?.[SectionId.TRANSPORT],
  (transport, drafts) => {
    const merged = mergeDraftItems(transport, "transport", drafts);
    const mergedTransport = merged?.transport as IEventrTransport[] | undefined;
    return mergedTransport?.filter((transport) => transport);
  },
);
export const selectCurrentItineraryTitle = createSelector(
  (state: BoundStore) => state.selectedItinerary,
  (itin) => {
    return itin?.v2?.find((s) => s.sectionID === 1)?.data?.title ?? "";
  },
);

export const selectEditItem = createSelector(
  (state: BoundStore) => state.editValues,
  (state: BoundStore) => state.draftSectionItems[state.selectedItinID],
  (state: BoundStore, sectionID: number) => sectionID,
  (state: BoundStore, sectionID: number, _id?: string) => _id,
  (editValues, itinDrafts, sectionID: number, _id?: string) => {
    if (sectionID) {
      const section = editValues?.[sectionID as unknown as keyof typeof editValues];

      // Deliberately not gated on the section existing: a draft is the only record of a
      // new item, and an itinerary with no saved items of this kind has no section here
      // at all. Gating on it made a new Activity invisible to its own edit form whenever
      // it was the first one. mergeDraftItems treats an absent container as empty.
      if (sectionID === SectionId.TRANSPORT) {
        const merged = mergeDraftItems(section, "transport", itinDrafts?.[SectionId.TRANSPORT]);
        return merged?.transport?.find((transport: { _id: string }) => transport._id === _id) ?? null;
      }
      if (sectionID === SectionId.ACTIVITY) {
        const merged = mergeDraftItems(section, "activities", itinDrafts?.[SectionId.ACTIVITY]);
        return merged?.activities?.find((activity: { _id: string }) => activity._id === _id) ?? null;
      }
      if (sectionID === SectionId.GENERAL) {
        // Not a list, so the draft is keyed on a fixed sentinel id rather than an item id.
        const draft = itinDrafts?.[SectionId.GENERAL]?.find((item) => item._id === "0");
        if (draft) return structuredCloneL(draft);
        return section ? structuredCloneL(section) : null;
      }
    }
    return null;
  },
);

export const selectAllPublishedTodos = createSelector(
  (state: BoundStore) => state.selectedItinerary,
  (itin) => {
    const todos: IEventrActivity[] =
      itin?.v2
        ?.find((s) => s.sectionID === SectionId.ACTIVITY)
        ?.data?.activities?.filter((a: IEventrActivity) => a.type === "todo") ?? [];
    return todos ?? [];
  },
);

export const selectFilterNonItineraryUsers = createSelector(
  (state: BoundStore) => state.selectedItinerary,
  (state: BoundStore, userList: IOptionsWithTypes[]) => userList,
  (itin, userList) => {
    const itinMembers: IOptionsWithTypes[] = itin?.v2?.find((s) => s.sectionID === SectionId.GENERAL)?.data?.members;
    const filteredMembers = userList?.filter((u) => {
      return itinMembers?.some((m) => m.option === u.option);
    });
    return filteredMembers ?? [];
  },
);

export const selectTodoListRange = createSelector(
  (state: BoundStore) => state.apiUpdates?.todos,
  (state: BoundStore, todoList: ITodoList[]) => todoList,
  (todoStatus, todoList: ITodoList[]) => {
    if (!todoList || !Array.isArray(todoList) || todoList.length === 0) {
      return "normal" as TodoRange;
    }

    // Start with the best status
    let worstStatus: TodoRange = "normal";

    // Check if all todos are completed
    const allCompleted = todoList.every((todo) => {
      // Check if todo has tasks and all tasks are completed
      if (todo.tasks && todo.tasks.length > 0) {
        return todo.tasks.every((task) => {
          if (!task._id) return true;
          return todoStatus?.[task._id]?.value?.status === "completed";
        });
      }
      return true; // Consider a todo with no tasks as completed
    });

    if (allCompleted) {
      return "done" as TodoRange;
    }
    const now = new Date().getTime();
    // Loop through each todo to find the worst status
    for (const todo of todoList) {
      if (!todo.due_date && todo.start_by) {
        const start_by = new EventrDate(todo.start_by);
        // Check if this todo is in warning zone (less than start_by)
        if (start_by.getUnixMs - now < 0) {
          // Set to warning, but keep checking for possible danger status
          worstStatus = "warning";
        }
      }
      // Skip todos without due dates
      if (!todo.due_date) continue;

      const date = new EventrDate(todo.due_date);
      const start_by = new EventrDate(todo.start_by ?? todo.due_date);
      const diff = date.getUnixMs - now;

      const pendingTodos = (todo.tasks ?? []).filter((todo) => {
        if (!todo._id) return false;
        return todoStatus?.[todo._id] === undefined || todoStatus?.[todo._id]?.value?.status === "incomplete";
      });
      if (pendingTodos.length === 0) continue;

      // Check if this todo is in danger zone (past due date)
      if (diff < 0) {
        // If we find a danger status, we can return early since it's the worst
        return "danger" as TodoRange;
      }

      // Check if this todo is in warning zone (less than start_by)
      if (start_by.getUnixMs - now < 0) {
        // Set to warning, but keep checking for possible danger status
        worstStatus = "warning";
      }
    }

    return worstStatus;
  },
);
export const selectorSplitData = createSelector(
  (state: BoundStore) => state.selectedItinerary?.v2,
  (state: BoundStore) => state.orgConfig?.categories,
  // dataV2 is optional if we want to some other data
  (state: BoundStore, dataV2?: V2[]) => dataV2,
  (data, categories, dataV2) => {
    const dataToUse = dataV2 ?? data;

    if (!dataToUse) return { splitSections: [], categorized: [], todos: [] };
    const { splitSections, notImported } = splitImportedData(dataToUse, categories);
    const { allOtherData, todos } = splitTodosData(notImported);
    const categorized = categoriesActivities(allOtherData, categories);

    return { splitSections, categorized, todos };
  },
);
export const selectCategoryActivities = createSelector(
  (state: BoundStore, categoryID?: string, ogCode?: string, dataV2?: V2[]) => selectorSplitData(state, dataV2),
  (state: BoundStore, categoryID?: string) => categoryID,
  (state: BoundStore, categoryID?: string, ogCode?: string) => ogCode,
  ({ categorized, splitSections }, categoryID, ogCode) => {
    const mainCat = categorized.find((d) => {
      if (ogCode) {
        return d.category === categoryID && d.ogCode === ogCode;
      }
      return d.category === categoryID;
    });

    if (mainCat) {
      return mainCat;
    }
    if (splitSections) {
      for (const s of splitSections) {
        const data = s.data.find((d) => {
          if (ogCode) {
            return d.category === categoryID && d.ogCode === ogCode;
          }
          return d.category === categoryID;
        });
        if (data) {
          return data;
        }
      }
    }
    return mainCat;
  },
);

/**
 * Every favourited entry, wrapped in a copy of the Activities section so the favourites list is
 * rendered by the same page, renderers and grouping as an activity category.
 *
 * Favourites can only be set on activities (only the activity detail screen offers the toggle),
 * so the whole set is in section 8 — stands included, which is why this cannot be read off the
 * Up Next components: those drop anything undated.
 */
export const selectFavouriteActivities = createSelector(
  (state: BoundStore) => state.selectedItinerary?.v2,
  (state: BoundStore) => state.favourites,
  (v2, favourites) => {
    const section = v2?.find((d) => d.sectionID === SectionId.ACTIVITY);
    if (!section) return undefined;

    const activities = ((section.data?.activities ?? []) as IEventrActivity[]).filter(
      (activity) => activity._id && isFavourited(favourites, activity._id),
    );

    return { ...section, usePage: true, data: { ...section.data, activities } } as V2;
  },
);

/** How many entries the favourites list holds, for the breakout rows that link to it. */
export const selectFavouriteCount = createSelector(
  selectFavouriteActivities,
  (section) => ((section?.data?.activities ?? []) as IEventrActivity[]).length,
);

export const selectIsItineraryEmpty = createSelector(
  (state: BoundStore) => state.itineraryComponents,
  (state: BoundStore) => state.getEventInfoV2(),
  (state: BoundStore) => selectorSplitData(state),
  (itineraryComponents, eventInfo) => {
    if (!itineraryComponents || !itineraryComponents.present) {
      return undefined; // Or potentially true/false depending on desired default behavior
    }
    if (Object.keys(itineraryComponents.present).length === 0) {
      return true;
    } else {
      if (
        Object.keys(itineraryComponents.present).length === 1 &&
        Object.keys(itineraryComponents.present)[0] === "Other" &&
        isIEventrDatePast(eventInfo?.data?.end_date)
      ) {
        return true;
      } else {
        return false;
      }
    }
  },
);

//MARK: Helpers
const getAllUsersCl = (path: string[], d: any): any[] => {
  return tryParseJSON(getV2ValueWP(path.join("."), d), []);
};

export const transformItineraryComponents = (itin: IItinerary, timeType: TZoneTypes, appUser: AppUser) => {
  const firstEntry = itin?.v2?.[0];
  const organisedDataRenderArray = firstEntry ? itin?.v2?.slice(1).concat(firstEntry) : undefined;
  let user: AppUser | null = null;
  if (useGuestStore.getState().isGuestMode) {
    user = { id: -1 } as AppUser;
  } else {
    user = appUser;
  }

  if (user) {
    const comps: INextComponent[] = [];
    const pastComps: INextComponent[] = [];
    const todos: any[] = [];
    const onlyUpNextData = (organisedDataRenderArray || []).filter((d) => {
      return d.rendererOptions.settings.displayInUpNext;
    });

    onlyUpNextData.forEach((sectionData) => {
      const filteredByUser = useBoundStore
        .getState()
        .filterEntitiesUserSpecific("personal", sectionData, sectionData.rendererOptions.settings.userList);

      /// Remove pending items for itinerary page
      const filteredOutPending = filteredByUser.filter(filterOutPendingTag);

      const upNextSectionList = generateUpNextList(
        user,
        filteredOutPending,
        sectionData.rendererOptions,
        "upNext",
        timeType,
        sectionData,
        useLanguageContext.getState().timeFormat,
      );

      if (upNextSectionList) {
        comps.push(...upNextSectionList.present);
        pastComps.push(...upNextSectionList.past);
        todos.push(...upNextSectionList.todos);
      }
    });

    comps.forEach((entry) => {
      const start = entry.data?.start;
      const end = entry.data?.end;

      if (start === end) {
        entry.data.PIT = true;
      }
    });

    sortByDate(comps);
    sortByDate(pastComps);

    const groupPerDate = (list: INextComponent[]) => {
      const groupedData: { [key: string]: INextComponent[] } = {};
      list.forEach((item) => {
        if (item.date) {
          const date = item.date.split(" ")[0];
          if (!date) return;
          if (!groupedData[date]) {
            groupedData[date] = [];
          }
          groupedData[date].push(item);
        } else {
          if (!groupedData["Other"]) {
            groupedData["Other"] = [];
          }
          groupedData["Other"].push(item);
        }
      });
      return groupedData;
    };

    const presentObj = groupPerDate(comps);
    const pastObj = groupPerDate(pastComps);

    const itineraryComponents = {
      present: presentObj,
      past: pastObj,
      todos: todos,
    };

    return itineraryComponents;
  }
};

const generateItineraryComponents = (itin: IItinerary, timeType: TZoneTypes, appUser?: AppUser) => {
  const firstEntry = itin?.v2?.[0];
  const organisedDataRenderArray = firstEntry ? itin?.v2?.slice(1).concat(firstEntry) : undefined;
  let user: AppUser | null = null;
  if (useGuestStore.getState().isGuestMode) {
    user = { id: -1 } as AppUser;
  } else {
    user = appUser ?? null;
  }

  if (user) {
    const comps: INextComponent[] = [];
    const pastComps: INextComponent[] = [];
    const todos: ITodoList[] = [];
    const onlyUpNextData = (organisedDataRenderArray || []).filter((d) => {
      return d.rendererOptions.settings.displayInUpNext;
    });

    onlyUpNextData.forEach((sectionData) => {
      const filteredByUser = useBoundStore
        .getState()
        .filterEntitiesUserSpecific("personal", sectionData, sectionData.rendererOptions.settings.userList);

      /// Remove pending items for itinerary page
      const filteredOutPending = filteredByUser.filter(filterOutPendingTag);

      const upNextSectionList = generateUpNextList(
        user,
        filteredOutPending,
        sectionData.rendererOptions,
        "upNext",
        timeType,
        sectionData,
        useLanguageContext.getState().timeFormat,
      );

      if (upNextSectionList) {
        comps.push(...upNextSectionList.present);
        pastComps.push(...upNextSectionList.past);
        todos.push(...upNextSectionList.todos);
      }
    });

    sortByDate(comps);
    sortByDate(pastComps);

    const groupPerDate = (list: INextComponent[]) => {
      const groupedData: { [key: string]: INextComponent[] } = {};

      list.forEach((item) => {
        // Use the converted `date` field (already timezone-converted) for grouping
        // instead of `sortDate` (raw EventrDate with original event timezone).
        // This ensures events are grouped by the LOCAL date the user should see.
        const dateKey = item.date ? item.date.split(" ")[0] : null;

        if (dateKey) {
          if (!groupedData[dateKey]) {
            groupedData[dateKey] = [];
          }
          groupedData[dateKey].push(item);
        } else {
          if (!groupedData["Other"]) {
            groupedData["Other"] = [];
          }
          groupedData["Other"].push(item);
        }
      });

      return groupedData;
    };

    const presentObj = groupPerDate(comps);
    const pastObj = groupPerDate(pastComps);

    const itineraryComponents = {
      present: presentObj,
      past: pastObj,
      todos: todos,
    };

    useBoundStore.setState(
      {
        itineraryComponents,
      },
      false,
      "generateItineraryComponents",
    );
  }
};

/**
 * @deprecated use selectIsItineraryEmpty
 */
const setSelectedItinEmpty = (itineraryComponents: any) => {
  if (!itineraryComponents || !itineraryComponents.present) {
    useBoundStore.setState({ selectedItinEmpty: undefined }, false, "setSelectedItinEmpty");
    return;
  }
  if (Object.keys(itineraryComponents.present).length === 0) {
    useBoundStore.setState({ selectedItinEmpty: true }, false, "setSelectedItinEmpty");
  } else {
    if (
      Object.keys(itineraryComponents.present).length === 1 &&
      Object.keys(itineraryComponents.present)[0] === "Other" &&
      isPast(parseISO(bs().getEventInfoV2()?.data.end_date))
    ) {
      useBoundStore.setState({ selectedItinEmpty: true }, false, "setSelectedItinEmpty");
    } else {
      useBoundStore.setState({ selectedItinEmpty: false }, false, "setSelectedItinEmpty");
    }
  }
};
